import React, { useState, useEffect, useCallback } from "react";
import { makeAuthenticatedRequest } from "../../utils/apiRequest";
import { useAuth } from "../../contexts/authContext";

const TransportPopup = ({ isOpen, onClose, itineraryId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [transportData, setTransportData] = useState(null);
  const [apiResponseData, setApiResponseData] = useState(null);
  
  // Track currency per field (default: $)
  const [currencyMap, setCurrencyMap] = useState({});
  // Store USD rate for conversion (default 83 if not fetched)
  const [usdRate, setUsdRate] = useState(83); // fallback to 83 if not found
  // Store currency rates (for future use)
  const [currencyRates, setCurrencyRates] = useState([]);
  
  // Form state for transport details
  const [totalMileage, setTotalMileage] = useState(0);
  const [workingDays, setWorkingDays] = useState(0);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [mileagePerDay, setMileagePerDay] = useState(0); // fee_per_100km
  const [costPerKm, setCostPerKm] = useState(0); // additional_fee_per_km
  const [batta, setBatta] = useState(0); // bata_price
  const [numAdults, setNumAdults] = useState(0);
  const [numChildren, setNumChildren] = useState(0);
  
  // Additional editable fields
  const [other, setOther] = useState(0);
  const [guideFee, setGuideFee] = useState(0);
  const [guideAccommodation, setGuideAccommodation] = useState(0);
  const [selectedVehicleDetails, setSelectedVehicleDetails] = useState(null);

  // Vehicle list state
  const [vehicles, setVehicles] = useState([]);
  const [defaultVehicleObj, setDefaultVehicleObj] = useState(null);

  // Store days array for daily costing
  const [days, setDays] = useState([]);

  const [dayTaxIncluded, setDayTaxIncluded] = useState({});

  // Helper to get tax percentage from itinerary data
  const getTaxPercentage = () => {
    const itineraryData = apiResponseData?.data?.[0] || apiResponseData;
    return itineraryData?.taxes?.[0]?.tax_percentage || 0;
  }

  // Define fetchTransportData with useCallback to prevent recreation on each render
  const fetchTransportData = useCallback(async () => {
    if (!token || !itineraryId) return;
    
    console.log('TransportPopup: Starting to fetch transport data...');
    console.log('TransportPopup: Using itinerary ID:', itineraryId);
    
    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest(
        "https://us-central1-evoq-travel-craft.cloudfunctions.net/get_itinerary_data",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ itinerary_id: itineraryId }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('TransportPopup: API response received:', responseData);
      
      // Store the full response data in state for later use
      setApiResponseData(responseData);

      // The API response has data nested inside a 'data' array
      const itineraryData = responseData.data && responseData.data[0] ? responseData.data[0] : responseData;
      console.log('TransportPopup: Extracted itinerary data:', itineraryData);

      // Extract USD rate from itineraryData.rates
      if (Array.isArray(itineraryData.rates)) {
        const usdObj = itineraryData.rates.find(r => r.currency_name && r.currency_name.includes("USD"));
        if (usdObj && usdObj.value) {
          setUsdRate(Number(usdObj.value));
        } else {
          setUsdRate(0); // fallback
        }
      } else {
        setUsdRate(0); // fallback
      }

      // Extract transport data from the response
      if (itineraryData) {
        // --- Build days array with saved costing data ---
     // --- Build days array with saved costing data ---
let newCurrencyMap = {};
let newDayTaxIncluded = {}; // Add this line

let mergedDays = (itineraryData.days || []).map((day, idx) => {
  // Find the corresponding dayObj in itinerary_cost (by index)
  const dayCostObj = Array.isArray(itineraryData.itinerary_cost) && itineraryData.itinerary_cost[idx];
  let merged = { ...day };

  // Try to find a transport_costing object for this day
  if (dayCostObj && Array.isArray(dayCostObj.transport_costing)) {
    const transportCost = dayCostObj.transport_costing.find(tc => tc.type === "Transport");
    if (transportCost) {
      // Extract saved values and currency
      merged.daily_mileage = transportCost.totalMileage ?? merged.daily_mileage;
      merged.other = (typeof transportCost.other === 'object'
        ? parseFloat(transportCost.other.value)
        : parseFloat(transportCost.other)) ?? 0;
      merged.otherTaxIncluded = (typeof transportCost.other === 'object' ? transportCost.other.tax_include : false);
      merged.otherCurrency = (typeof transportCost.other === 'object' ? transportCost.other.currency : '$');
      merged.otherBase = (typeof transportCost.other === 'object' ? transportCost.other.value : transportCost.other) ?? merged.other;
      merged.guideFee = (typeof transportCost.guideFee === 'object' ? transportCost.guideFee.value : transportCost.guideFee) ?? merged.guideFee;
      merged.guideFeeTaxIncluded = (typeof transportCost.guideFee === 'object' ? transportCost.guideFee.tax_include : false);
      merged.guideFeeBase = (typeof transportCost.guideFee === 'object' ? transportCost.guideFee.value : transportCost.guideFee) ?? merged.guideFee;
      merged.guideAccommodation = transportCost.guideAccommodation ?? merged.guideAccommodation;
      merged.totalCost = transportCost.totalCost;
      
      // Load tax state from saved data
      if (transportCost.tax && typeof transportCost.tax === 'object') {
        newDayTaxIncluded[idx] = transportCost.tax.tax_included || false;
      }
      
      // Set currencyMap for this day's fields
      if (typeof transportCost.other === 'object' && transportCost.other.currency) {
        newCurrencyMap[`other_${idx}`] = transportCost.other.currency;
      }
      if (typeof transportCost.guideFee === 'object' && transportCost.guideFee.currency) {
        newCurrencyMap[`guideFee_${idx}`] = transportCost.guideFee.currency;
      }
    }
  }
  return merged;
});

setDays(mergedDays);
setCurrencyMap(prev => ({ ...prev, ...newCurrencyMap }));
setDayTaxIncluded(newDayTaxIncluded); // Add this line
        // Check if vehicle details are nested inside bookingDetails
        const vehicleDetails = itineraryData.bookingDetails?.vehicleDetails || {};
        console.log('TransportPopup: Vehicle details from API:', vehicleDetails);
        
        // Extract numAdults and numChildren from bookingDetails (string), fallback to top-level or 0
        const numAdults = itineraryData.bookingDetails?.numAdults !== undefined
          ? parseInt(itineraryData.bookingDetails.numAdults, 10) || 0
          : (itineraryData.numAdults || 0);
        const numChildren = itineraryData.bookingDetails?.numChildren !== undefined
          ? parseInt(itineraryData.bookingDetails.numChildren, 10) || 0
          : (itineraryData.numChildren || 0);

        // Set the transport data from the API response
        const extractFeeValue = (fee) => (typeof fee === 'object' && fee !== null && 'value' in fee ? fee.value : fee || 0);
const transportInfo = {
  totalMileage: itineraryData.total_mileage || 0,
  workingDays: itineraryData.no_of_days || itineraryData.days?.length || 0,
  selectedVehicle: vehicleDetails.vehicleType || itineraryData.vehicleType || '',
  mileagePerDay: extractFeeValue(vehicleDetails.fee_per_100km) || extractFeeValue(itineraryData.fee_per_100km),
  costPerKm: extractFeeValue(vehicleDetails.additional_fee_per_km) || extractFeeValue(itineraryData.additional_fee_per_km),
  batta: extractFeeValue(vehicleDetails.bata_price) || extractFeeValue(itineraryData.bata_price),
  numAdults,
  numChildren
};
        
        console.log('TransportPopup: Extracted transport data:', transportInfo);
        
        // Update the state with the extracted data
        setTransportData(transportInfo);
        setTotalMileage(transportInfo.totalMileage);
        setWorkingDays(transportInfo.workingDays);
        setSelectedVehicle(transportInfo.selectedVehicle);
        setMileagePerDay(transportInfo.mileagePerDay);
        setCostPerKm(transportInfo.costPerKm);
        setBatta(transportInfo.batta);
        setNumAdults(transportInfo.numAdults);
        setNumChildren(transportInfo.numChildren);
      } else {
        console.log('TransportPopup: No valid transport data found in response');
        // If no data is returned or it's not in expected format, set default values
        setTransportData(null);
      }
    } catch (err) {
      console.error('TransportPopup: Error fetching transport data:', err.message);
      setError(err.message);
      setTransportData(null);
    } finally {
      console.log('TransportPopup: Transport data fetch completed');
      setLoading(false);
    }
  }, [token, itineraryId]);



  // Load transport data when component mounts and isOpen changes
  useEffect(() => {
    if (isOpen && itineraryId) {
      fetchTransportData();
    }
  }, [isOpen, itineraryId, fetchTransportData]);

  // Fetch vehicles on open
  useEffect(() => {
    const fetchVehicles = async () => {
      if (!token || !isOpen) return;
      try {
        const response = await makeAuthenticatedRequest(
          "https://us-central1-evoq-travel-craft.cloudfunctions.net/get_vehicle_data",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setVehicles(Array.isArray(data) ? data : data.data || []);
      } catch (err) {
        console.error('Error fetching vehicles:', err);
        setVehicles([]);
      }
    };
    fetchVehicles();
  }, [token, isOpen]);

  // Store the default vehicle object for dropdown
  useEffect(() => {
    if (!selectedVehicle || !vehicles.length) return;
    const found = vehicles.find(v => v.vehicleType === selectedVehicle || v.model === selectedVehicle);
    if (!found && selectedVehicle) {
      // If default vehicle is not in fetched vehicles, create a minimal object
      setDefaultVehicleObj({ vehicleType: selectedVehicle, model: selectedVehicle });
    } else {
      setDefaultVehicleObj(null);
    }
  }, [selectedVehicle, vehicles]);

  // Print transport cost data object (type: 'Transport') for debugging
  useEffect(() => {
    if (apiResponseData && Array.isArray(apiResponseData.itinerary_cost)) {
      const transportObj = apiResponseData.itinerary_cost.find(x => x.type === "Transport");
      if (transportObj) {
        console.log("Transport Cost Data:", transportObj);
      }
    }
  }, [apiResponseData]);

  // Calculate daily transport cost for a given mileage
  // Updated calculation: correct per-day batta, correct fee per 100km
  // Use displayed value for Fee (FIRST 100KM) in total calculation
  // Utility: calculate amount with/without tax
  const getAmountWithTax = (base, taxIncluded) => {
    const taxPercentage = getTaxPercentage();
    if (taxIncluded) {
      // Value already includes tax, do not add again
      return parseFloat(base).toFixed(2);
    } else {
      // Add tax if not included
      return (parseFloat(base) * (1 + taxPercentage / 100)).toFixed(2);
    }
  };

 const calculateDailyCost = (dailyMileage, displayedFirst100kmFee, otherOverride, guideFeeOverride, dayIndex) => {
  // Always use USD for calculations
  const costPerKmValue = (typeof costPerKm === 'object' && costPerKm !== null && 'value' in costPerKm ? parseFloat(costPerKm.value) : parseFloat(costPerKm) || 0) / usdRate;
  const battaValue = (typeof batta === 'object' && batta !== null && 'value' in batta ? parseFloat(batta.value) : parseFloat(batta) || 0) / usdRate;
  const workingDaysValue = parseInt(workingDays) || 0;
  const otherValue = otherOverride !== undefined ? parseFloat(otherOverride) : parseFloat(other) || 0;
  const guideFeeValue = guideFeeOverride !== undefined ? parseFloat(guideFeeOverride) : parseFloat(guideFee) || 0;

  // displayedFirst100kmFee is in Rs, convert to $ for calculation
  const first100kmFeeUSD = (parseFloat(displayedFirst100kmFee) || 0) / usdRate;
  // Fee for additional km
  const additionalKm = Math.max(0, dailyMileage - 100);
  const additionalKmFeeUSD = additionalKm * costPerKmValue;
  // Batta for each day: just the per-day batta value
  const battaTotalUSD = battaValue;
  
  // Calculate tax on first 100km + additional km fees if tax is included for this day
  const transportFeesUSD = first100kmFeeUSD + additionalKmFeeUSD;
  const taxPercentage = getTaxPercentage();
  const taxIncluded = dayTaxIncluded[dayIndex] || false;
  const taxAmountUSD = taxIncluded ? (transportFeesUSD * taxPercentage / 100) : 0;
  
  return {
    first100kmFee: first100kmFeeUSD,
    additionalKmFee: additionalKmFeeUSD,
    batta: battaTotalUSD,
    other: otherValue,
    guideFee: guideFeeValue,
    taxAmount: taxAmountUSD,
    total: transportFeesUSD + battaTotalUSD + otherValue + guideFeeValue + taxAmountUSD,
  };
};




  // Calculate total transport cost (sum of all days)
  const calculateTotalCost = () => {
    if (!days || days.length === 0) {
      // fallback to old logic if days not loaded
      const totalMileageValue = parseInt(totalMileage) || 0;
      const workingDaysValue = parseInt(workingDays) || 0;
      const mileagePerDayValue = parseFloat(mileagePerDay) || 0;
      const costPerKmValue = parseFloat(costPerKm) || 0;
      const battaValue = parseFloat(batta) || 0;
      const otherValue = parseFloat(other) || 0;
      const guideFeeValue = parseFloat(guideFee) || 0;
      const guideAccommodationValue = parseFloat(guideAccommodation) || 0;
      const baseMileageCost = 100 * mileagePerDayValue;
      const additionalDistance = Math.max(0, totalMileageValue - 100);
      const additionalDistanceCost = additionalDistance * costPerKmValue;
      const battaCost = battaValue * workingDaysValue;
      return baseMileageCost + additionalDistanceCost + battaCost + otherValue + guideFeeValue + guideAccommodationValue;
    }
    return days.reduce((sum, day) => {
  const dailyMileage = parseFloat(day.daily_mileage) || 0;
  const otherValue = day.other !== undefined ? parseFloat(day.other) : parseFloat(other) || 0;
  const guideFeeValue = day.guideFee !== undefined ? parseFloat(day.guideFee) : parseFloat(guideFee) || 0;
  return sum + calculateDailyCost(dailyMileage, mileagePerDay, otherValue, guideFeeValue).total;
}, 0);
  };

  
  // Function to save changes to the API
  const saveChanges = async () => {
    try {
      setUpdating(true);
      console.log('TransportPopup: Saving updated transport data');
      
      // Get the full itinerary data from the API response to preserve other data
      const itineraryData = apiResponseData?.data?.[0] || apiResponseData || {};
      
      // Create a deep copy of the itinerary data to avoid mutating the original
      const updatedItineraryData = JSON.parse(JSON.stringify(itineraryData));
      
      // Update the top-level properties for backward compatibility
      updatedItineraryData.total_mileage = totalMileage;
      updatedItineraryData.vehicleType = selectedVehicle;
      updatedItineraryData.fee_per_100km = mileagePerDay;
      updatedItineraryData.additional_fee_per_km = costPerKm;
      updatedItineraryData.bata_price = batta;
      updatedItineraryData.numAdults = numAdults;
      updatedItineraryData.numChildren = numChildren;

      // Ensure the nested structure exists
      if (!updatedItineraryData.bookingDetails) {
        updatedItineraryData.bookingDetails = {};
      }
      if (!updatedItineraryData.bookingDetails.vehicleDetails) {
        updatedItineraryData.bookingDetails.vehicleDetails = {};
      }

      // Update numAdults and numChildren as strings inside bookingDetails
      updatedItineraryData.bookingDetails.numAdults = String(numAdults);
      updatedItineraryData.bookingDetails.numChildren = String(numChildren);

      // Update the nested vehicle details
      updatedItineraryData.bookingDetails.vehicleDetails.vehicleType = selectedVehicle;
      updatedItineraryData.bookingDetails.vehicleDetails.fee_per_100km = mileagePerDay;
      updatedItineraryData.bookingDetails.vehicleDetails.additional_fee_per_km = costPerKm;
      updatedItineraryData.bookingDetails.vehicleDetails.bata_price = batta;

      // --- TRANSPORT COST LOGIC ---
      // For each day, create a separate transportCostObj using per-day values
     // --- TRANSPORT COST LOGIC ---
// For each day, create a separate transportCostObj using per-day values
if (Array.isArray(updatedItineraryData.itinerary_cost) && Array.isArray(days)) {
  updatedItineraryData.itinerary_cost.forEach((dayObj, idx) => {
    const day = days[idx];
    if (!day) return; // skip if no matching day

    // Per-day values (fallback to state if not present in day)
    const dailyMileage = parseFloat(day.daily_mileage) || 0;
    const otherValue = day.other !== undefined ? parseFloat(day.other) : parseFloat(other) || 0;
    const guideFeeValue = day.guideFee !== undefined ? parseFloat(day.guideFee) : parseFloat(guideFee) || 0;
    // Use base if present, else fallback
    const otherBase = day.otherBase !== undefined ? parseFloat(day.otherBase) : otherValue;
    const guideFeeBase = day.guideFeeBase !== undefined ? parseFloat(day.guideFeeBase) : guideFeeValue;
    // Apply tax if included
    const otherFinal = day.otherTaxIncluded ? otherBase * (1 + getTaxPercentage() / 100) : otherBase;
    const guideFeeFinal = day.guideFeeTaxIncluded ? guideFeeBase * (1 + getTaxPercentage() / 100) : guideFeeBase;
    const guideAccommodationValue = day.guideAccommodation !== undefined ? parseFloat(day.guideAccommodation) : parseFloat(guideAccommodation) || 0;
    const displayedFirst100kmFee = parseFloat(mileagePerDay) || 0;

    // Calculate costs for this day including tax
    const additionalKm = Math.max(0, dailyMileage - 100);
    const additionalKmFee = additionalKm * (parseFloat(costPerKm) || 0);
    const battaTotal = parseFloat(batta) || 0;
    
    // Calculate tax details
    const transportFeesRs = displayedFirst100kmFee + additionalKmFee;
    const taxPercentage = getTaxPercentage();
    const taxIncluded = dayTaxIncluded[idx] || false;
    const taxAmountRs = taxIncluded ? (transportFeesRs * taxPercentage / 100) : 0;
    
    const total = transportFeesRs + battaTotal + otherValue + guideFeeValue + taxAmountRs;
const transportCostObj = {
  type: "Transport",
  totalMileage: dailyMileage,
  workingDays,
  selectedVehicle,
  // Add complete vehicle details
  vehicleDetails: selectedVehicleDetails ? {
    additional_fee_per_km: selectedVehicleDetails.additional_fee_per_km,
    bata_price: selectedVehicleDetails.bata_price,
    category: selectedVehicleDetails.category,
    fee_per_100km: selectedVehicleDetails.fee_per_100km,
    luggage_space: selectedVehicleDetails.luggage_space,
    make: selectedVehicleDetails.make,
    maximum_seating_capacity: selectedVehicleDetails.maximum_seating_capacity,
    model: selectedVehicleDetails.model,
    seats: selectedVehicleDetails.seats,
    vehicle_category: selectedVehicleDetails.vehicle_category,
    vehicle_img: selectedVehicleDetails.vehicle_img,
    vehicle_status: selectedVehicleDetails.vehicle_status,
    vehicle_type_id: selectedVehicleDetails.vehicle_type_id,
    vehicle_version: selectedVehicleDetails.vehicle_version
  } : null,
  mileagePerDay,
  costPerKm,
  batta: battaTotal,
  numAdults,
  numChildren,
  other: {
    value: String(day.other ?? 0),
    tax_include: !!day.otherTaxIncluded,
    currency: day.otherCurrency || currencyMap[`other_${idx}`] || '$'
  },
  guideFee: {
    value: day.guideFeeTaxIncluded ? getAmountWithTax(day.guideFeeBase ?? guideFeeValue, true) : (day.guideFeeBase ?? guideFeeValue),
    tax_include: !!day.guideFeeTaxIncluded,
    currency: currencyMap[`guideFee_${idx}`] || '$'
  },
  guideAccommodation: guideAccommodationValue,
  additionalKmFee,
  // Tax details
  tax: {
    tax_included: taxIncluded,
    tax_percentage: taxPercentage,
    tax_amount: taxAmountRs,
    tax_applies_to: "transport_fees",
    base_amount: transportFeesRs
  },
  totalCost: total,
  updatedAt: new Date().toISOString(),
  manually_updated: true
};
    // For debugging: print the new structure
    console.log('TransportPopup: transport_costing for day', idx + 1, JSON.stringify(transportCostObj, null, 2));

    if (!Array.isArray(dayObj.transport_costing)) {
      dayObj.transport_costing = [];
    }
    // Remove existing transport costing for this day (if you want only one per day)
    dayObj.transport_costing = dayObj.transport_costing.filter(tc => tc.type !== "Transport");
    dayObj.transport_costing.push(transportCostObj);
  });
}
      // Print the itinerary_cost array
      console.log("TransportPopup: itinerary_cost array:", updatedItineraryData.itinerary_cost);

      // Print total fee for first 100km, total fee for additional km, and total of batta
      const totalFirst100kmFee = 100 * (parseFloat(mileagePerDay) || 0);
      const additionalDistance = Math.max(0, (parseInt(totalMileage) || 0) - (100 * (parseInt(workingDays) || 0)));
      const totalAdditionalKmFee = additionalDistance * (parseFloat(costPerKm) || 0);
      const totalBatta = (parseFloat(batta) || 0) * (parseInt(workingDays) || 0);
      console.log('Total fee for first 100km:', totalFirst100kmFee);
      console.log('Total fee for additional km:', totalAdditionalKmFee);
      console.log('Total of batta:', totalBatta);

      // Create the final API payload with the updates wrapper
      const apiPayload = {
        itinerary_id: itineraryId,
        updates: {
          ...updatedItineraryData,
          price_change: true
        }
      };
      
      console.log('TransportPopup: Formatted API payload:', apiPayload);
      
      // Make the API call to update the itinerary data
      const response = await makeAuthenticatedRequest(
        "https://us-central1-evoq-travel-craft.cloudfunctions.net/update_itinerary_data",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(apiPayload),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to update transport data: ${response.status} - ${errorData.error || "Unknown error"}`);
      }
      
      const result = await response.json();
      console.log('TransportPopup: Successfully updated transport data:', result);
      
      // Show success message
      // alert("Transport data has been successfully updated!");
      
      // Close the popup when done
      onClose();
    } catch (error) {
      console.error('TransportPopup: Error saving transport data:', error);
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  // Add this after your existing useEffects, before the calculateDailyCost function

// Prevent body scroll when popup is open
useEffect(() => {
  if (isOpen) {
    // Save current scroll position
    const scrollY = window.scrollY;
    
    // Prevent scrolling on body
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Restore scroll when component unmounts or popup closes
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      
      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }
}, [isOpen]);

// Also handle cleanup when component unmounts
useEffect(() => {
  return () => {
    // Ensure body scroll is restored when component unmounts
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
  };
}, []);
  
  // Format numbers to 2 decimal places with safety check
  const formatCurrency = (value) => {
    // Ensure value is a number and handle null/undefined
    if (value === null || value === undefined || isNaN(value)) {
      return '0.00';
    }
    const numValue = parseFloat(value);
    return isNaN(numValue) ? '0.00' : numValue.toFixed(2);
  };
  
  // Handle currency toggle
  const handleCurrencyToggle = (fieldId) => {
    setCurrencyMap((prev) => {
      const key = `${fieldId}`;
      const current = prev[key] || '$';
      const newCurrency = current === '$' ? 'RS' : '$';
      return { ...prev, [key]: newCurrency };
    });
  };
  
  // Convert amount based on currency
  const convertAmount = (amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;
    
    if (fromCurrency === '$' && toCurrency === 'RS') {
      return amount * usdRate;
    } else if (fromCurrency === 'RS' && toCurrency === '$') {
      return amount / usdRate;
    }
    
    return amount;
  };
  
  // Get display amount based on selected currency
  const getDisplayAmount = (amount, fieldId) => {
    const currency = currencyMap[`${fieldId}`] || '$';
    if (currency === 'RS') {
      return convertAmount(amount, '$', 'RS');
    }
    return amount;
  };

  if (!isOpen) return null;
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white border border-blue-200 rounded-lg shadow-xl p-6 w-full max-w-md">
          <p className="text-blue-800 text-center">Loading transport data...</p>
        </div>
      </div>
    );
  }

  if (updating) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white border border-blue-200 rounded-lg shadow-xl p-6 w-full max-w-md">
          <p className="text-blue-800 text-center">Updating Transport Data...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white border border-blue-200 rounded-lg shadow-xl p-6 w-full max-w-md">
          <h2 className="text-red-600 text-lg font-semibold mb-2">Error</h2>
          <p className="text-gray-800 mb-4">{error}</p>
          <button 
            onClick={onClose}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 transition-opacity duration-300 ease-in-out">
      <div 
        className="bg-white border border-blue-200 rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] relative transform transition-all duration-300 ease-in-out overflow-hidden flex flex-col" 
        style={{ 
          background: 'linear-gradient(to bottom right, #ffffff, #f7fbff)',
          boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.5), 0 8px 10px -6px rgba(59, 130, 246, 0.3)'
        }}>
        {/* Close icon */}
        <button 
          onClick={onClose}
          className="absolute top-2 right-2 text-blue-500 hover:text-blue-700 transition-colors z-10"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Header */}
        <div className="p-4 border-b border-blue-200 sticky top-0 bg-white z-10 shadow-sm">
          <h2 className="text-blue-800 text-center font-bold text-xl uppercase tracking-wider">TRANSPORT</h2>
        </div>
        
        {/* Scrollable content container */}   
        <div className="flex-1 overflow-hidden p-4">
          <div className="overflow-auto max-h-[calc(90vh-180px)] rounded-lg p-4">
            {/* Input fields section - structured like the image */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <div className="w-32 font-semibold text-blue-800">Total Mileage</div>
                <div className="mx-2">:</div>
              <input
  type="number"
  value={totalMileage}
  className="w-64 p-1 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
  placeholder="800 KM"
  min="0"
  readOnly
  disabled
/>
              </div>
              <div className="flex items-center mb-4">
                <div className="w-32 font-semibold text-blue-800">Working Days</div>
                <div className="mx-2">:</div>
               <input
  type="number"
  value={workingDays}
  className="w-64 p-1 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
  placeholder="5 Days"
  min="0"
  readOnly
  disabled
/>
              </div>
              <div className="flex items-center mb-4">
                <div className="w-32 font-semibold text-blue-800">Adults</div>
                <div className="mx-2">:</div>
                <input
                  type="number"
                  value={numAdults}
                  onChange={(e) => setNumAdults(e.target.value)}
                  className="w-64 p-1 border border-blue-300 rounded"
                  placeholder="Number of Adults"
                  min="0"
                />
              </div>
              <div className="flex items-center mb-4">
                <div className="w-32 font-semibold text-blue-800">Children</div>
                <div className="mx-2">:</div>
                <input
                  type="number"
                  value={numChildren}
                  onChange={(e) => setNumChildren(e.target.value)}
                  className="w-64 p-1 border border-blue-300 rounded"
                  placeholder="Number of Children"
                  min="0"
                />
              </div>
              <div className="flex items-center mb-4">
                <div className="w-32 font-semibold text-blue-800">Selected Vehicle</div>
                <div className="mx-2">:</div>
              <select
  value={selectedVehicle}
  onChange={e => {
    const val = e.target.value;
    setSelectedVehicle(val);
    
    // Find vehicle object by make+model
    const vObj = vehicles.find(v => `${v.make || ''} ${v.model || ''}`.trim() === val);
    if (vObj) {
      // Store the complete vehicle details
      setSelectedVehicleDetails(vObj);
      setMileagePerDay(vObj.fee_per_100km || 0);
      setCostPerKm(vObj.additional_fee_per_km || 0);
      setBatta(vObj.bata_price || 0);
    } else if (defaultVehicleObj && `${defaultVehicleObj.make || ''} ${defaultVehicleObj.model || ''}`.trim() === val) {
      // If selected is default vehicle, keep existing rates
      setSelectedVehicleDetails(defaultVehicleObj);
    } else {
      setSelectedVehicleDetails(null);
    }
  }}
  className="w-64 p-1 border border-blue-300 rounded"
>
                  <option value="">Select a vehicle</option>
                  {/* Always show the default vehicle if not present in fetched list */}
                  {defaultVehicleObj && (
                    <option value={`${defaultVehicleObj.make || ''} ${defaultVehicleObj.model || ''}`.trim()}>
                      {`${defaultVehicleObj.make || ''} ${defaultVehicleObj.model || ''}`.trim()}
                    </option>
                  )}
                  {vehicles.map((v, idx) => {
                    const name = `${v.make || ''} ${v.model || ''}`.trim();
                    return (
                      <option key={name || idx} value={name}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            
            {/* Transport Costing Totals by Day */}
            {apiResponseData && Array.isArray(apiResponseData.itinerary_cost) && (
              <div className="mb-4 p-4 bg-yellow-50 rounded border border-yellow-200">
                <h4 className="font-semibold text-yellow-700 mb-2">Transport Costing Totals by Day</h4>
                {apiResponseData.itinerary_cost.map((dayObj, i) => (
                  Array.isArray(dayObj.transport_costing) && dayObj.transport_costing.length > 0 && (
                    <div key={i} className="mb-2">
                      <div className="font-semibold text-yellow-800 mb-1">
                        {dayObj.day ? `${dayObj.day} (${dayObj.city || ''})` : `Day ${i + 1}`}
                      </div>
                      {dayObj.transport_costing.map((tc, j) => {
                        const mileagePerDay = parseFloat(tc.mileagePerDay) || 0;
                        const totalMileage = parseFloat(tc.totalMileage) || 0;
                        const costPerKm = parseFloat(tc.costPerKm) || 0;
                        const batta = parseFloat(tc.batta) || 0;
                        const workingDays = parseFloat(tc.workingDays) || 0;
                        const totalFirst100kmFee = 100 * mileagePerDay;
                        const additionalDistance = Math.max(0, totalMileage - 100);
                        const totalAdditionalKmFee = additionalDistance * costPerKm;
                        const totalBatta = batta * workingDays;
                        return (
                          <ul key={j} className="text-yellow-900 text-sm grid grid-cols-2 gap-x-8 gap-y-1 mb-2">
                            <li><span className="font-semibold">Fee (FIRST 100KM):</span> {totalFirst100kmFee.toFixed(2)}</li>
                            <li><span className="font-semibold">Fee (ADDITIONAL PER KM):</span> {totalAdditionalKmFee.toFixed(2)}</li>
                            <li><span className="font-semibold">Batta:</span> {totalBatta.toFixed(2)}</li>
                          </ul>
                        );
                      })}
                    </div>
                  )
                ))}
              </div>
            )}
            {/* User-friendly Transport Cost Data display */}
            {apiResponseData && Array.isArray(apiResponseData.itinerary_cost) && (
              (() => {
                const transportObj = apiResponseData.itinerary_cost.find(x => x.type === "Transport");
                if (!transportObj) return null;
                return (
                  <div className="mb-4 p-4 bg-green-50 rounded border border-green-200">
                    <h4 className="font-semibold text-green-700 mb-2">Transport Cost Details</h4>
                    <ul className="text-green-900 text-sm grid grid-cols-2 gap-x-8 gap-y-1">
                      {Object.entries(transportObj).map(([key, value]) => (
                        <li key={key}>
                          <span className="font-semibold">{key}:</span> {String(value)}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()
            )}
            {/* Debug: Show Transport Cost Data (object 3 in itinerary_cost array) */}
            {apiResponseData && Array.isArray(apiResponseData.itinerary_cost) && (
              <div className="mb-4 p-2 bg-blue-50 rounded border border-blue-200">
                <h4 className="font-semibold text-blue-700 mb-2">Transport Cost Data (Debug)</h4>
                <pre className="text-xs text-blue-900 bg-blue-100 p-2 rounded overflow-auto">
                  {JSON.stringify(apiResponseData.itinerary_cost.find(x => x.type === "Transport"), null, 2)}
                </pre>
              </div>
            )}
            {/* Daily Transport Cost Table */}
            {days && days.length > 0 && (
              <div className="mb-8 p-4 bg-blue-50 rounded border border-blue-200">
        

            {/* Print transport_costing details for each day */}
            {apiResponseData && Array.isArray(apiResponseData.itinerary_cost) && apiResponseData.itinerary_cost.map((dayObj, idx) => (
              Array.isArray(dayObj.transport_costing) && dayObj.transport_costing.length > 0 && (
                <div key={idx} className="mb-4 p-4 bg-gray-50 rounded border border-gray-200">
                  <div className="font-semibold text-gray-700 mb-2">
                    {dayObj.day ? `${dayObj.day} (${dayObj.city || ''})` : `Day ${idx + 1}`}
                  </div>
                  <table className="w-full text-xs md:text-sm mb-2">
                   <thead>
  <tr className="bg-gray-100">
    <th className="border px-2 py-1">Daily Mileage</th>
    <th className="border px-2 py-1">Fee (FIRST 100KM)</th>
    <th className="border px-2 py-1">Fee (ADDITIONAL PER KM)</th>
    <th className="border px-2 py-1">Tax</th>
    <th className="border px-2 py-1">Batta</th>
    <th className="border px-2 py-1">Other</th>
    <th className="border px-2 py-1">Guide Fee</th>
    <th className="border px-2 py-1">Guide Accommodation</th>
    <th className="border px-2 py-1">Total</th>
  </tr>
</thead>
                    <tbody>
  {dayObj.transport_costing.map((tc, j) => (
    <tr key={j}>
      <td className="border px-2 py-1 text-center">{tc.totalMileage}</td>
      <td className="border px-2 py-1 text-right">{tc.mileagePerDay !== undefined ? tc.mileagePerDay : ''}</td>
      <td className="border px-2 py-1 text-right">{tc.totalAdditionalKmFee !== undefined ? tc.totalAdditionalKmFee : ''}</td>
      <td className="border px-2 py-1 text-center">
        {tc.tax && tc.tax.tax_included ? (
          <div className="text-xs">
            <div className="text-green-600 font-semibold">✓ Included</div>
            <div className="text-gray-500">{tc.tax.tax_percentage}%</div>
            <div className="text-blue-600">{formatCurrency(tc.tax.tax_amount)} Rs</div>
          </div>
        ) : (
          <div className="text-xs text-gray-400">Not Included</div>
        )}
      </td>
      <td className="border px-2 py-1 text-right">{tc.batta !== undefined ? tc.batta : ''}</td>
      <td className="border px-2 py-1 text-right">
        {(() => {
          let base = 0;
          if (typeof tc.other === 'object' && tc.other !== null) {
            base = parseFloat(tc.other.value) || 0;
          } else if (tc.other !== undefined) {
            base = parseFloat(tc.other) || 0;
          }
          return (
            <>
              {formatCurrency(base)} <span className="text-xs">Rs</span>
              <br />
              <span className="text-[10px] text-blue-400">(${formatCurrency(base / usdRate)})</span>
            </>
          );
        })()}
      </td>
      <td className="border px-2 py-1 text-right">
        {(() => {
          let base = 0;
          if (typeof tc.guideFee === 'object' && tc.guideFee !== null) {
            base = parseFloat(tc.guideFee.value) || 0;
          } else if (tc.guideFee !== undefined) {
            base = parseFloat(tc.guideFee) || 0;
          }
          return (
            <>
              {formatCurrency(base)} <span className="text-xs">Rs</span>
              <br />
              <span className="text-[10px] text-blue-400">(${formatCurrency(base / usdRate)})</span>
            </>
          );
        })()}
      </td>
      <td className="border px-2 py-1 text-right">{tc.guideAccommodation !== undefined ? tc.guideAccommodation : ''}</td>
     <td className="border px-2 py-1 text-right font-bold">
  {tc.totalCost !== undefined ? (
    <div className="flex flex-col items-end">
      <div className="text-blue-800">
        {(tc.other && tc.other.currency === 'RS')
          ? `$${formatCurrency(tc.totalCost)}`
          : `$${formatCurrency(tc.totalCost)}`}
      </div>
      <div className="text-xs text-gray-500" style={{lineHeight:1}}>
        rs {(tc.other && tc.other.currency === 'RS')
          ? formatCurrency(tc.totalCost * usdRate)
          : formatCurrency(tc.totalCost * usdRate)}
      </div>
    </div>
  ) : ''}
</td>
    </tr>
  ))}
</tbody>
                  </table>
                </div>
              )
            ))}

                <h4 className="font-semibold text-blue-700 mb-4">Daily Transport Cost Table</h4>
                <div className="overflow-auto">
                 <table className="w-full min-w-[1200px] border text-xs md:text-sm">
                  <thead>
  <tr className="bg-blue-100">
    <th className="border border-blue-200 px-2 py-1 text-blue-700 font-bold">Day</th>
    <th className="border border-blue-200 px-2 py-1 text-blue-700 font-bold">City</th>
    <th className="border border-blue-200 px-2 py-1 text-blue-700 font-bold">Daily Mileage</th>
    <th className="border border-blue-200 px-2 py-1 text-blue-700 font-bold">Fee (FIRST 100KM)</th>
    <th className="border border-blue-200 px-2 py-1 text-blue-700 font-bold">Fee (ADDITIONAL PER KM)</th>
    <th className="border border-blue-200 px-2 py-1 text-blue-700 font-bold">Tax</th>
    <th className="border border-blue-200 px-2 py-1 text-blue-700 font-bold">Batta</th>
    <th className="border border-blue-200 px-2 py-1 text-blue-700 font-bold">Other</th>
    <th className="border border-blue-200 px-2 py-1 text-blue-700 font-bold">Guide Fee</th>
    <th className="border border-blue-200 px-2 py-1 text-blue-700 font-bold">Total</th>
  </tr>
</thead>
                   <tbody>
  {days.map((day, idx) => {
    // Always use the displayed value and selected currency for calculation
    const dailyMileage = day.daily_mileage === '' || day.daily_mileage === undefined || day.daily_mileage === null ? '' : parseFloat(day.daily_mileage) || 0;
    // Always extract the USD value from state for calculation
    const otherUSD = parseFloat(day.other) || 0;
    const guideFeeUSD = parseFloat(day.guideFee) || 0;
    const guideAccommodationValue = day.guideAccommodation !== undefined ? parseFloat(day.guideAccommodation) : parseFloat(guideAccommodation) || 0;
    // Use the displayed value in the Fee (FIRST 100KM) column for total calculation
    const getFeeValue = (val) => (typeof val === 'object' && val !== null && 'value' in val ? Number(val.value) : Number(val) || 0);
    const displayedFirst100kmFee = getFeeValue(mileagePerDay);
    const displayedCostPerKm = getFeeValue(costPerKm);
    const displayedBatta = getFeeValue(batta);
    const costs = calculateDailyCost(dailyMileage, displayedFirst100kmFee, otherUSD, guideFeeUSD, idx);
    return (
      <tr key={idx} className="even:bg-blue-50 odd:bg-blue-100">
        <td className="border px-2 py-1 text-center">{day.day_number}</td>
        <td className="border px-2 py-1 text-center">{day.city?.[0]?.city_name || ""}</td>
        <td className="border px-2 py-1 text-center">
        <input
  type="number"
  className="w-16 p-1 border border-blue-300 rounded text-center text-xs"
  value={dailyMileage === 0 ? '' : dailyMileage}
  onChange={e => {
    const newMileage = e.target.value;
    setDays(prevDays => prevDays.map((d, i) => i === idx ? { ...d, daily_mileage: newMileage } : d));
  }}
/>
        </td>
        <td className="border px-2 py-1 text-right">
          {formatCurrency(displayedFirst100kmFee)} <span className="text-xs">Rs</span>
          <br />
          <span className="text-[10px] text-gray-500">Fee per 100km</span>
          <br />
          <span className="text-[10px] text-blue-400">(${formatCurrency(displayedFirst100kmFee / usdRate)})</span>
        </td>
        <td className="border px-2 py-1 text-right">
          {(() => {
            const additionalKm = Math.max(0, parseFloat(day.daily_mileage) - 100);
            const additionalKmFeeRs = additionalKm * displayedCostPerKm;
            return (
              <>
                {formatCurrency(additionalKmFeeRs)} <span className="text-xs">Rs</span>
                <br />
                <span className="text-[10px] text-gray-500">({formatCurrency(displayedCostPerKm)} × {additionalKm})</span>
                <br />
                <span className="text-[10px] text-blue-400">(${formatCurrency(additionalKmFeeRs / usdRate)})</span>
              </>
            );
          })()}
        </td>
        <td className="border px-2 py-1 text-center">
          <div className="flex flex-col items-center gap-1">
            <input
              type="checkbox"
              checked={!!dayTaxIncluded[idx]}
              onChange={e => {
                setDayTaxIncluded(prev => ({
                  ...prev,
                  [idx]: e.target.checked
                }));
              }}
            />
            <span className="text-[10px] text-gray-500">Include Tax</span>
            {dayTaxIncluded[idx] && (
              <div className="text-xs text-center">
                <div className="text-blue-600 font-semibold">
                  {formatCurrency(costs.taxAmount * usdRate)} <span className="text-xs">Rs</span>
                </div>
                <div className="text-[10px] text-blue-400">
                  (${formatCurrency(costs.taxAmount)})
                </div>
                <div className="text-[10px] text-gray-500">
                  ({getTaxPercentage()}% tax)
                </div>
              </div>
            )}
          </div>
        </td>
<td className="border px-2 py-1 text-right">
  {formatCurrency(displayedBatta)} <span className="text-xs">Rs</span>
  <br />
  <span className="text-[10px] text-gray-500">Per Day</span>
  <br />
  <span className="text-[10px] text-blue-400">(${formatCurrency(displayedBatta / usdRate)})</span>
</td>
      <td className="border px-2 py-1 text-right">
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-1">
            {/* Currency toggle button */}
            <button
              type="button"
              onClick={() => handleCurrencyToggle(`other_${idx}`)}
              className={`relative w-12 h-6 flex items-center rounded-full focus:outline-none transition border border-blue-300 ${((currencyMap[`other_${idx}`] || '$') === '$') ? 'bg-blue-200' : 'bg-green-200'}`}
              title="Toggle currency"
              style={{ minWidth: 48 }}
            >
              <span className={`absolute left-1 text-xs font-bold transition-colors ${(currencyMap[`other_${idx}`] || '$') === '$' ? 'text-blue-900' : 'text-gray-400'}`}>$</span>
              <span className={`absolute right-1 text-xs font-bold transition-colors ${(currencyMap[`other_${idx}`] || '$') === 'RS' ? 'text-green-900' : 'text-gray-400'}`}>RS</span>
              <span
                className={`absolute transition-transform duration-200 ease-in-out bg-white h-5 w-5 rounded-full shadow ${((currencyMap[`other_${idx}`] || '$') === '$') ? 'translate-x-0' : 'translate-x-6'}`}
                style={{ left: 0, top: 0.5 }}
              ></span>
            </button>
            
            <div className="flex items-center gap-1">
              <span className="text-xs">{currencyMap[`other_${idx}`] || '$'}</span>
              <input
  type="number"
  className="w-16 p-1 border border-blue-300 rounded text-center text-xs"
  value={
    (() => {
      let val = (currencyMap[`other_${idx}`] || '$') === 'RS'
        ? convertAmount(day.other, '$', 'RS')
        : day.other;
      // Always fallback to empty if zero/empty/undefined/null/NaN
      if (val === null || val === undefined || val === '' || isNaN(val) || Number(val) === 0) return '';
      return String(Number(val));
    })()
  }
  onChange={e => {
    const val = e.target.value;
    // Allow empty string for controlled input (shows empty box while typing), fallback to 0 otherwise
    const valNum = val === '' ? '' : (parseFloat(val) || 0);
    let otherUSD = 0;
    if ((currencyMap[`other_${idx}`] || '$') === 'RS') {
      otherUSD = valNum === '' ? '' : convertAmount(valNum, 'RS', '$');
    } else {
      otherUSD = valNum;
    }
    setDays(prevDays =>
      prevDays.map((d, i) => {
        if (i !== idx) return d;
        return {
          ...d,
          other: otherUSD, // always store USD value or ''
          otherRawInput: valNum // track user's raw input
        };
      })
    );
  }}
/>
            </div>
            
            <input
              type="checkbox"
              checked={!!day.otherTaxIncluded}
              onChange={e => {
  const taxPercentage = getTaxPercentage();
  setDays(prevDays => prevDays.map((d, i) => {
    if (i !== idx) return d;
    const newTaxIncluded = !d.otherTaxIncluded;
    // Always recalculate from the user's raw input in the chosen currency
    const inputVal = d.otherRawInput !== undefined ? parseFloat(d.otherRawInput) : 0;
    let baseUSD = 0;
    if ((currencyMap[`other_${idx}`] || '$') === 'RS') {
      baseUSD = convertAmount(inputVal, 'RS', '$');
    } else {
      baseUSD = inputVal;
    }
    let newUSD = newTaxIncluded ? baseUSD * (1 + taxPercentage / 100) : baseUSD;
    return {
      ...d,
      otherTaxIncluded: newTaxIncluded,
      other: newUSD,
      otherBase: baseUSD
    };
  }));
}}
            />
            <span className="text-[10px] text-gray-500">Tax incl.</span>
            {day.otherTaxIncluded && (
              <span className="text-[10px] text-blue-500">({getTaxPercentage()}% tax)</span>
            )}
          </div>
          
          {/* Show opposite currency for reference */}
          {(currencyMap[`other_${idx}`] || '$') === 'RS' && (
            <div className="text-xs text-gray-500 block text-right" style={{lineHeight:1}}>
              <span>$ {formatCurrency(day.other)}</span>
            </div>
          )}
          {(currencyMap[`other_${idx}`] || '$') === '$' && (
  <div className="text-xs text-gray-500 block text-right" style={{lineHeight:1}}>
    <span>RS {formatCurrency(convertAmount(day.other, '$', 'RS'))}</span>
  </div>
)}
        </div>
      </td>
      <td className="border px-2 py-1 text-right">
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-1">
            {/* Currency toggle button */}
            <button
              type="button"
              onClick={() => handleCurrencyToggle(`guideFee_${idx}`)}
              className={`relative w-12 h-6 flex items-center rounded-full focus:outline-none transition border border-blue-300 ${((currencyMap[`guideFee_${idx}`] || '$') === '$') ? 'bg-blue-200' : 'bg-green-200'}`}
              title="Toggle currency"
              style={{ minWidth: 48 }}
            >
              <span className={`absolute left-1 text-xs font-bold transition-colors ${(currencyMap[`guideFee_${idx}`] || '$') === '$' ? 'text-blue-900' : 'text-gray-400'}`}>$</span>
              <span className={`absolute right-1 text-xs font-bold transition-colors ${(currencyMap[`guideFee_${idx}`] || '$') === 'RS' ? 'text-green-900' : 'text-gray-400'}`}>RS</span>
              <span
                className={`absolute transition-transform duration-200 ease-in-out bg-white h-5 w-5 rounded-full shadow ${((currencyMap[`guideFee_${idx}`] || '$') === '$') ? 'translate-x-0' : 'translate-x-6'}`}
                style={{ left: 0, top: 0.5 }}
              ></span>
            </button>
            
            <div className="flex items-center gap-1">
              <span className="text-xs">{currencyMap[`guideFee_${idx}`] || '$'}</span>
              <input
  type="number"
  className="w-16 p-1 border border-blue-300 rounded text-center text-xs"
  value={
    (() => {
      let val = (currencyMap[`guideFee_${idx}`] || '$') === 'RS'
        ? convertAmount(day.guideFee, '$', 'RS')
        : day.guideFee;
      // Always fallback to empty if zero/empty/undefined/null/NaN
      if (val === null || val === undefined || val === '' || isNaN(val) || Number(val) === 0) return '';
      return String(Number(val));
    })()
  }
  onChange={e => {
    const val = e.target.value;
    // Allow empty string for controlled input (shows empty box while typing), fallback to 0 otherwise
    const valNum = val === '' ? '' : (parseFloat(val) || 0);
    setDays(prevDays =>
      prevDays.map((d, i) => {
        if (i !== idx) return d;
        let guideFeeUSD = 0;
        if ((currencyMap[`guideFee_${idx}`] || '$') === 'RS') {
          guideFeeUSD = valNum === '' ? '' : convertAmount(valNum, 'RS', '$');
        } else {
          guideFeeUSD = valNum;
        }
        return {
          ...d,
          guideFee: guideFeeUSD, // always store USD value or ''
          guideFeeRawInput: valNum // track user's raw input
        };
      })
    );
  }}
/>
            </div>
            
            <input
              type="checkbox"
              checked={!!day.guideFeeTaxIncluded}
              onChange={e => {
  const taxPercentage = getTaxPercentage();
  setDays(prevDays => prevDays.map((d, i) => {
    if (i !== idx) return d;
    const newTaxIncluded = !d.guideFeeTaxIncluded;
    // Always recalculate from the user's raw input in the chosen currency
    const inputVal = d.guideFeeRawInput !== undefined ? parseFloat(d.guideFeeRawInput) : 0;
    let baseUSD = 0;
    if ((currencyMap[`guideFee_${idx}`] || '$') === 'RS') {
      baseUSD = convertAmount(inputVal, 'RS', '$');
    } else {
      baseUSD = inputVal;
    }
    let newUSD = newTaxIncluded ? baseUSD * (1 + taxPercentage / 100) : baseUSD;
    return {
      ...d,
      guideFeeTaxIncluded: newTaxIncluded,
      guideFee: newUSD,
      guideFeeBase: baseUSD
    };
  }));
}}
            />
            <span className="text-[10px] text-gray-500">Tax incl.</span>
            {day.guideFeeTaxIncluded && (
              <span className="text-[10px] text-blue-500">({getTaxPercentage()}% tax)</span>
            )}
          </div>
          
          {/* Show opposite currency for reference */}
          {(currencyMap[`guideFee_${idx}`] || '$') === '$' && (
  <div className="text-xs text-gray-500 block text-right" style={{lineHeight:1}}>
    <span>RS {formatCurrency(convertAmount(day.guideFee, '$', 'RS'))}</span>
  </div>
)}
{(currencyMap[`guideFee_${idx}`] || '$') === 'RS' && (
  <div className="text-xs text-gray-500 block text-right" style={{lineHeight:1}}>
    <span>$ {formatCurrency(day.guideFee)}</span>
  </div>
)}
        </div>
      </td>
      
     <td className="border px-2 py-1 text-right font-bold">
  <div className="flex flex-col items-end">
    <div className="text-blue-800">
      ${formatCurrency(costs.total)}
    </div>
    <div className="text-xs text-gray-500" style={{lineHeight:1}}>
      Rs {formatCurrency(costs.total * usdRate)}
    </div>
  </div>
</td>
    </tr>
  );
})}
                    </tbody>
                 <tfoot>
  <tr className="bg-blue-200 font-bold">
    <td className="border border-blue-200 px-2 py-1 text-right" colSpan={9}>Grand Total</td>
    <td className="border border-blue-200 px-2 py-1 text-right">
      {/* Grand Total: sum of the per-row displayed totals, matching row logic and currency */}
      {(() => {
        let grandTotalUSD = 0;
        days.forEach((day, idx) => {
          const dailyMileage = parseFloat(day.daily_mileage) || 0;
          const otherUSD = parseFloat(day.other) || 0;
          const guideFeeUSD = parseFloat(day.guideFee) || 0;
          const guideAccommodationValue = day.guideAccommodation !== undefined ? parseFloat(day.guideAccommodation) : parseFloat(guideAccommodation) || 0;
          // Use the displayed value in the Fee (FIRST 100KM) column for total calculation
          const getFeeValue = (val) => (typeof val === 'object' && val !== null && 'value' in val ? Number(val.value) : Number(val) || 0);
          const displayedFirst100kmFee = getFeeValue(mileagePerDay);
          const costs = calculateDailyCost(dailyMileage, displayedFirst100kmFee, otherUSD, guideFeeUSD, idx);
          grandTotalUSD += costs.total;
        });
        return (
          <div className="flex flex-col items-end">
            <div className="text-blue-800">
              ${formatCurrency(grandTotalUSD)}
            </div>
            <div className="text-xs text-gray-600" style={{lineHeight:1}}>
              Rs {formatCurrency(grandTotalUSD * usdRate)}
            </div>
          </div>
        );
      })()}
    </td>
  </tr>
</tfoot>
                  </table>
                </div>
              </div>
            )}
            {/* Rates and Amount Table */}
            {/* <div className="mb-8">
              <h3 className="text-center font-semibold text-blue-800 mb-4">Rates</h3>
              
              <div className="grid grid-cols-3 gap-4 mb-2">
                <div className="font-semibold text-blue-800">Fee ( FIRST 100KM  )</div>
                <div className="text-center">
                  <input
                    type="text"
                    value={mileagePerDay}
                    onChange={(e) => setMileagePerDay(e.target.value)}
                    className="w-full p-1 border border-blue-300 rounded text-center"
                    placeholder="XXX"
                  />
                  <div className="text-xs text-gray-500">
                    100 KM × ${mileagePerDay}
                  </div>
                </div>
                <div className="text-right">
                  ${formatCurrency(100 * mileagePerDay)}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-2">
                <div className="font-semibold text-blue-800">Fee (ADDITIONAL PER KM)</div>
                <div className="text-center">
                  <input
                    type="text"
                    value={costPerKm}
                    onChange={(e) => setCostPerKm(e.target.value)}
                    className="w-full p-1 border border-blue-300 rounded text-center"
                    placeholder="XXX"
                  />
                  <div className="text-xs text-gray-500">
                    {Math.max(0, parseInt(totalMileage) - 100)} KM × ${costPerKm}
                  </div>
                </div>
                <div className="text-right">
                  ${formatCurrency(Math.max(0, parseInt(totalMileage) - 100) * costPerKm)}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-2">
                <div className="font-semibold text-blue-800">Batta</div>
                <div className="text-center">
                  <input
                    type="text"
                    value={batta}
                    onChange={(e) => setBatta(e.target.value)}
                    className="w-full p-1 border border-blue-300 rounded text-center"
                    placeholder="XXX"
                  />
                  <div className="text-xs text-gray-500">${batta} × {workingDays} Days</div>
                </div>
                <div className="text-right">
                  ${formatCurrency(batta * workingDays)}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-2">
                <div className="font-semibold text-blue-800">Other</div>
                <div className="text-center">
                  <input
                    type="number"
                    value={other}
                    onChange={(e) => setOther(e.target.value)}
                    className="w-full p-1 border border-blue-300 rounded text-center"
                    placeholder="XXX"
                  />
                </div>
                <div className="text-right">${formatCurrency(other)}</div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-2">
                <div className="font-semibold text-blue-800">Guide Fee</div>
                <div className="text-center">
                  <input
                    type="number"
                    value={guideFee}
                    onChange={(e) => setGuideFee(e.target.value)}
                    className="w-full p-1 border border-blue-300 rounded text-center"
                    placeholder="XXX"
                  />
                </div>
                <div className="text-right">${formatCurrency(guideFee)}</div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-2">
                <div className="font-semibold text-blue-800">Guide Accommodation</div>
                <div className="text-center">
                  <input
                    type="number"
                    value={guideAccommodation}
                    onChange={(e) => setGuideAccommodation(e.target.value)}
                    className="w-full p-1 border border-blue-300 rounded text-center"
                    placeholder="XXX"
                  />
                </div>
                <div className="text-right">${formatCurrency(guideAccommodation)}</div>
              </div>
              
              {/* 
              <div className="border-t border-blue-300 mt-4 pt-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 font-semibold text-blue-800">Cost of Transport  </div>
                  <div className="text-right font-bold">${formatCurrency(calculateTotalCost())}</div>
                </div>
              </div>
              */}
            {/* </div> */} 
          </div>
        </div>
        
        {/* Save and Cancel buttons - fixed at bottom */}
        <div className="p-4 border-t border-blue-200 flex justify-end space-x-3 bg-white sticky bottom-0 shadow-md z-10">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-blue-300 text-blue-600 rounded hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Cancel
          </button>
          <button
            onClick={saveChanges}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// Remove number input spinners for all browsers (Webkit & Firefox)
// This style will be injected at the top of the popup for local effect only
const noSpinnerStyle = `
  input[type=number]::-webkit-inner-spin-button, 
  input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type=number] {
    -moz-appearance: textfield; /* Firefox */
  }
`;

function TransportPopupWithNoSpinner(props) {
  return (
    <>
      <style>{noSpinnerStyle}</style>
      <TransportPopup {...props} />
    </>
  );
}

export default TransportPopupWithNoSpinner;